import { useMutation } from '@apollo/client';
import {
  Button,
  Checkbox,
  Flex,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import React, { useContext, useEffect, useState } from 'react';
import {
  FieldError,
  FieldValues,
  SubmitHandler,
  useForm,
} from 'react-hook-form';
import { AiFillCamera } from 'react-icons/ai';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import cms from '../../../apis/cms';
import {
  CREATE_GROUP_ELEMENT,
  UPDATE_GROUP_ELEMENT,
} from '../../../graphQL/mutations';
import { GET_PAGE_DATA } from '../../../graphQL/queries';
import Input from '../../Form/Input';
import Upload from '../../Form/Upload';
import RedirectForm, { redirectFormSchema } from '../../FormGroup/RedirectForm';
import context from '../context';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onCreated?: (item: any) => void;
  onEdited?: (item: any) => void;
  onDelete?: () => void;
  Item?: null | any;
}

interface FormData {
  name: string;
  redirect_value:
    | string
    | {
        label: string | number;
        value: string | number;
      };

  redirect_type: {
    label: string | number;
    value: string | number;
  };
  facets?: string;
  text_color?: string;
  highlighted: boolean;
  file?: FileList;
  file_id?: string;
}

const createFormData = yup
  .object()
  .shape({
    name: yup.string().required('El nombre es obligatorio'),
    highlighted: yup.boolean(),
  })
  .concat(redirectFormSchema);

const ItemForm: React.FC<Props> = ({
  onClose,
  isOpen,
  Item,
  onCreated,
  onEdited,
}) => {
  const form = useForm({
    resolver: yupResolver(createFormData),
  });
  const { formState, handleSubmit, register, setValue, reset } = form;

  const [createGroupElement] = useMutation(CREATE_GROUP_ELEMENT, {
    refetchQueries: [GET_PAGE_DATA, 'GetPageData'],
  });

  const [updateGroupElement] = useMutation(UPDATE_GROUP_ELEMENT, {
    refetchQueries: [GET_PAGE_DATA, 'GetPageData'],
  });

  const { pageGroup, element } = useContext(context);

  const toast = useToast();
  const { errors } = formState;
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  useEffect(() => {
    if (!Item) return;
    setValue('name', Item.name);
    setValue('highlighted', Item?.highlighted ?? false);
    setValue('text_color', Item?.text_color);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Item, setValue]);

  const handleForm: SubmitHandler<FormData> = async data => {
    try {
      const { ...rest } = data;
      setLoading(true);

      let fileReturn;
      if (data?.file && data?.file[0]) {
        const multipart = new FormData();
        multipart.append('file', data.file[0]);
        const { data: FileApi } = await cms.rest.post<any>(
          '/upload',
          multipart,
        );
        fileReturn = FileApi;
      }

      if (!Item) {
        const body = {
          ...rest,
          name: data?.name,
          facets: data?.facets,
          page_group_id: pageGroup?.id,
          highlighted: data?.highlighted ?? false,
          element_id: element?.id,
          redirect_value:
            typeof data?.redirect_value === 'object'
              ? data?.redirect_value?.value
              : data?.redirect_value,
          redirect_type: data?.redirect_type?.value,
          text_color: data.text_color !== '#000000' ? data.text_color : null,
          file_id: fileReturn ? fileReturn?.id : null,
        };

        if (fileReturn) {
          body.file_id = fileReturn.id;
        }

        delete body.file;

        const groupNewElement = await createGroupElement({
          variables: {
            createGroupElementInput: body,
          },
        });

        onClose();
        if (onCreated) onCreated(groupNewElement?.data?.createGroupElement);
        toast({
          title: t('success'),
          description: t('itemCreated'),
          status: 'success',
          position: 'top-right',
        });
      } else {
        const body = {
          ...rest,
          id: Item?.id,
          name: data?.name,
          facets: data?.facets,
          highlighted: data?.highlighted ?? false,
          page_group_id: pageGroup?.id,
          element_id: element?.id,
          redirect_value:
            typeof data?.redirect_value === 'object'
              ? data?.redirect_value?.value
              : data?.redirect_value,
          redirect_type: data?.redirect_type?.value,
          text_color: data.text_color !== '#000000' ? data.text_color : null,
        };

        if (fileReturn) {
          body.file_id = fileReturn.id;
        }

        delete body.file;

        const groupNewElement = await updateGroupElement({
          variables: {
            updateGroupElementInput: body,
          },
        });

        onClose();
        if (onEdited) onEdited(groupNewElement?.data?.updateGroupElement);
        toast({
          title: t('success'),
          description: t('itemUpdated'),
          status: 'success',
          position: 'top-right',
        });
      }
    } catch (err: any) {
      toast({
        title: 'Opss!!',
        description: err?.response?.data?.message || t('generalError'),
        status: 'error',
        position: 'top-right',
      });
    } finally {
      setLoading(false);
      reset();
      onClose();
    }
  };

  return (
    <Modal
      onClose={() => {
        onClose();
        reset();
      }}
      isOpen={isOpen}
      isCentered
      size="xl"
    >
      <ModalOverlay />
      <ModalContent
        maxH="90svh"
        overflowY="scroll"
        as="form"
        onSubmit={handleSubmit(handleForm as SubmitHandler<FieldValues>)}
      >
        <ModalHeader>Link</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={8}>
            <Flex
              style={{
                display: 'flex',
                justifyContent: 'center',
                width: '100%',
              }}
            >
              <Input
                label={t('nameField')}
                placeholder={t('nameField')}
                error={errors.name as FieldError | undefined}
                {...register('name')}
                mr={2}
              />
              <Input
                {...register('text_color')}
                type="color"
                label="Cor do link"
                width="180px"
                placeholder="Cor do texto (opcional)"
              />
            </Flex>
            <Flex
              style={{
                display: 'flex',
                justifyContent: 'left',
                width: '100%',
              }}
            >
              <Checkbox
                alignSelf="flex-start"
                colorScheme="brand"
                {...register('highlighted')}
              >
                {t('highlight')}
              </Checkbox>
            </Flex>
            <RedirectForm pageElement={Item} {...form} />
            <Upload
              label="Icone"
              acceptedFileTypes="image/*"
              alignSelf="center"
              icon={AiFillCamera}
              {...register('file')}
              url={Item?.file?.id ? `${Item?.file?.url}` : ''}
              idFileApi={Item?.file?.id}
            />
          </VStack>
        </ModalBody>
        <ModalFooter>
          <HStack spacing={4}>
            <Button
              onClick={() => {
                onClose();
                reset();
              }}
            >
              {t('close')}
            </Button>
            <Button colorScheme="brand" isLoading={loading} type="submit">
              {t('save')}
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ItemForm;
